import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import {
  Edit,
  Trash2,
  Calendar,
  User,
  Briefcase,
  FileText,
  Plus,
  Download,
  X,
  Check,
  AlertTriangle,
} from "lucide-react";
import Navbar from "../../../../components/Navbar";
import api from "../../../../services/api";
import "./ProjectPage.css";
import { toast } from "react-toastify";
import { Link } from "react-router-dom";

const ProjectPage = ({
  role,
  onEdit,
  onDeleteSuccess,
  onOpenCAF,
  onOpenPFR,
}) => {
  const { projectId } = useParams();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showDocModal, setShowDocModal] = useState(false);
  const [docTitle, setDocTitle] = useState("");
  const [docFile, setDocFile] = useState(null);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [documentsLoading, setDocumentsLoading] = useState(false);
  const [createdDocuments, setCreatedDocuments] = useState({
    caf: [],
    pfr: [],
    emp: [],
    other: [],
  });
  const [selectedAdmin, setSelectedAdmin] = useState("");
  const [adminsList, setAdminsList] = useState([]);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [documentForApproval, setDocumentForApproval] = useState(null);
  const [showApprovalActionModal, setShowApprovalActionModal] = useState(false);
  const [documentToApprove, setDocumentToApprove] = useState(null);
  const [approvalAction, setApprovalAction] = useState(null);
  const [approvalComment, setApprovalComment] = useState("");
  const [pendingApprovalRequests, setPendingApprovalRequests] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchProject = async () => {
      try {
        const response = await api.get(`/projects/${projectId}`);

        if (response.data && response.data.project) {
          setProject(response.data.project);
        } else {
          throw new Error("Invalid project data structure");
        }
        setLoading(false);
      } catch (err) {
        console.error("Error fetching project:", err);
        setError(`Failed to load project details: ${err.message}`);
        setLoading(false);
      }
    };

    fetchProject();
    fetchProjectDocuments();
    fetchCreatedDocuments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  useEffect(() => {
    const fetchAdmins = async () => {
      try {
        const response = await api.get("/api/users/admins");
        if (response.data) {
          setAdminsList(response.data);
        }
      } catch (error) {
        console.error("Error fetching admin users:", error);
      }
    };

    fetchAdmins();
  }, []);

  const fetchCreatedDocuments = async () => {
    try {
      const response = await api.get(
        `/api/projects/${projectId}/created-documents`
      );
      if (response.data && response.data.documents) {
        const docsByType = {
          caf: response.data.documents.filter((doc) => doc.type === "caf"),
          pfr: response.data.documents.filter((doc) => doc.type === "pfr"),
          emp: response.data.documents.filter((doc) => doc.type === "emp"),
          other: response.data.documents.filter(
            (doc) => doc.type === "other" || !doc.type
          ),
        };
        setCreatedDocuments(docsByType);
      }
    } catch (err) {
      console.error("Error fetching created documents:", err);
    }
  };

  const fetchProjectDocuments = async () => {
    try {
      setDocumentsLoading(true);
      const response = await api.get(`/api/projects/${projectId}/documents`);
      if (response.data && response.data.documents) {
        setDocuments(response.data.documents);
      }
    } catch (err) {
      console.error("Error fetching project documents:", err);
    } finally {
      setDocumentsLoading(false);
    }
  };

  const handleEdit = () => {
    if (onEdit) {
      onEdit();
    }
  };

  const handleDelete = async () => {
    if (window.confirm("Are you sure you want to delete this project?")) {
      try {
        await api.delete(`/projects/${projectId}`);
        if (onDeleteSuccess) {
          onDeleteSuccess();
        }
      } catch (err) {
        console.error("Delete error:", err);
        alert(
          "Failed to delete project: " +
            (err.response?.data?.message || err.message)
        );
      }
    }
  };

  const handleDocumentTypeSelect = (docType) => {
    if (
      docType === "pfr" &&
      createdDocuments.pfr &&
      createdDocuments.pfr.length > 0
    ) {
      if (
        window.confirm(
          "A PFR document already exists for this project. Do you want to edit it instead of creating a new one?"
        )
      ) {
        navigate(`/${role}/pfr?projectId=${project.project_id}`);
        return;
      } else {
        // User canceled, don't navigate
        return;
      }
    }
    switch (docType) {
      case "caf":
        navigate(`/${role}/caf?projectId=${project.project_id}`);
        break;
      case "pfr":
        navigate(`/${role}/pfr?projectId=${project.project_id}`);
        break;
      case "emp":
        navigate(`/${role}/emp?projectId=${project.project_id}`);
        break;
      default:
        console.log(`Document type ${docType} not yet implemented`);
    }
  };

  const handleDownloadPfrDocument = async (pfrId) => {
    try {
      const loadingToast = toast.info("Preparing document for download...", {
        autoClose: false,
      });

      const response = await api.get(`/api/pfr/${pfrId}`);

      if (!response.data || !response.data.document) {
        toast.dismiss(loadingToast);
        toast.error("Failed to find PFR document data");
        return;
      }

      const pfrContent = response.data.document.content;
      const docResponse = await api.post("/generate-doc", pfrContent, {
        responseType: "blob",
      });

      toast.dismiss(loadingToast);
      const filename = `PFR_${
        response.data.document.title || "Document"
      }_${new Date().toISOString().slice(0, 10)}.docx`;

      const url = window.URL.createObjectURL(new Blob([docResponse.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();

      toast.success("Document downloaded successfully");
    } catch (error) {
      console.error("Error downloading PFR document:", error);
      toast.error("Failed to download PFR document");
    }
  };

  const handleDownloadEmpDocument = async (empId) => {
    try {
      const loadingToast = toast.info("Preparing document for download...", {
        autoClose: false,
      });

      const response = await api.get(`/api/emp/${empId}`);

      if (!response.data || !response.data.document) {
        toast.dismiss(loadingToast);
        toast.error("Failed to find EMP document data");
        return;
      }

      const empContent = response.data.document.content;
      const docResponse = await api.post("/generate-doc", empContent, {
        responseType: "blob",
      });

      toast.dismiss(loadingToast);
      const filename = `EMP_${
        response.data.document.title || "Document"
      }_${new Date().toISOString().slice(0, 10)}.docx`;

      const url = window.URL.createObjectURL(new Blob([docResponse.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();

      toast.success("Document downloaded successfully");
    } catch (error) {
      console.error("Error downloading EMP document:", error);
      toast.error("Failed to download EMP document");
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setDocFile(e.target.files[0]);
    }
  };

  const clearSelectedFile = () => {
    setDocFile(null);
    const fileInput = document.getElementById("docFile");
    if (fileInput) fileInput.value = "";
  };

  const handleDocumentUpload = async (e) => {
    e.preventDefault();
    if (!docFile) {
      alert("Please select a file");
      return;
    }

    try {
      setUploadingDoc(true);
      const formData = new FormData();
      formData.append("file", docFile);
      formData.append("title", docTitle);
      formData.append("project_id", projectId);

      await api.post(`/api/projects/${projectId}/documents`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      setDocFile(null);
      setDocTitle("");
      setShowDocModal(false);

      await fetchProjectDocuments();
    } catch (err) {
      console.error("Error uploading document:", err);
      alert(
        "Failed to upload document: " +
          (err.response?.data?.message || err.message)
      );
    } finally {
      setUploadingDoc(false);
    }
  };

  const handleDownloadDocument = async (docId, fileName) => {
    try {
      const response = await api.get(`/api/documents/${docId}/download`, {
        responseType: "blob",
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error("Error downloading document:", err);
      alert("Failed to download document");
    }
  };

  const handleDeleteDocument = async (documentId, documentType) => {
    let confirmMessage = `Are you sure you want to delete this ${documentType.toUpperCase()} document?`;

    // If it's an approved document, add a warning
    if (
      documentType === "pfr" &&
      createdDocuments.pfr &&
      createdDocuments.pfr.length > 0 &&
      createdDocuments.pfr[0].approval_status === "approved"
    ) {
      confirmMessage = `This ${documentType.toUpperCase()} document has been approved. Are you sure you want to delete it? This action cannot be undone.`;
    }

    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      // Add loading toast
      const loadingToast = toast.info("Deleting document...", {
        autoClose: false,
      });

      const response = await api.delete(`/api/${documentType}/${documentId}`);

      toast.dismiss(loadingToast);

      if (response.data && response.data.success) {
        if (documentType === "caf") {
          setCreatedDocuments((prev) => ({
            ...prev,
            caf: [],
          }));
        } else if (documentType === "pfr") {
          setCreatedDocuments((prev) => ({
            ...prev,
            pfr: [],
          }));
        }

        toast.success(
          `${documentType.toUpperCase()} document deleted successfully`
        );

        if (pendingApprovalRequests.length > 0) {
          setPendingApprovalRequests((prevRequests) =>
            prevRequests.filter(
              (req) =>
                !(
                  req.document_id === documentId &&
                  req.document_type === documentType
                )
            )
          );
        }
      } else {
        throw new Error("Failed to delete document");
      }
    } catch (error) {
      console.error(`Error deleting ${documentType} document:`, error);
      toast.error(`Failed to delete ${documentType.toUpperCase()} document`);
    }
  };

  const handleRequestApproval = (documentId, documentType) => {
    setDocumentForApproval({ id: documentId, type: documentType });
    setShowApprovalModal(true);
  };

  const submitApprovalRequest = async () => {
    if (!selectedAdmin || !documentForApproval) {
      toast.error("Please select an admin for approval");
      return;
    }

    try {
      await api.post("/api/approval-requests", {
        documentId: documentForApproval.id,
        documentType: documentForApproval.type,
        adminId: selectedAdmin,
        projectId: projectId,
      });

      toast.success("Approval request sent successfully");
      setShowApprovalModal(false);
      setSelectedAdmin("");
      setDocumentForApproval(null);

      // Refresh documents to show updated status
      fetchCreatedDocuments();
    } catch (error) {
      console.error("Error requesting approval:", error);
      toast.error("Failed to send approval request");
    }
  };

  useEffect(() => {
    const fetchApprovalRequests = async () => {
      if (role !== "admin") return;

      try {
        // First get all document types for this project
        const typesToCheck = [];

        if (createdDocuments.pfr && createdDocuments.pfr.length > 0) {
          typesToCheck.push({
            type: "pfr",
            id: createdDocuments.pfr[0].id,
          });
        }

        if (createdDocuments.caf && createdDocuments.caf.length > 0) {
          typesToCheck.push({
            type: "caf",
            id: createdDocuments.caf[0].id,
          });
        }

        // Fetch approval requests for each document
        const pendingRequests = [];

        for (const doc of typesToCheck) {
          const response = await api.get(
            `/api/approval-requests/document/${doc.type}/${doc.id}`
          );
          if (response.data && response.data.requests) {
            const pendingReqs = response.data.requests.filter(
              (req) => req.status === "pending"
            );
            pendingRequests.push(...pendingReqs);
          }
        }

        setPendingApprovalRequests(pendingRequests);
      } catch (error) {
        console.error("Error fetching approval requests:", error);
      }
    };

    if (createdDocuments) {
      fetchApprovalRequests();
    }
  }, [createdDocuments, role]);

  const handleApprovalAction = (docId, docType, action) => {
    setDocumentToApprove({ id: docId, type: docType });
    setApprovalAction(action);
    setShowApprovalActionModal(true);
  };

  const submitApprovalDecision = async () => {
    if (!documentToApprove || !approvalAction) {
      toast.error("Missing document or action information");
      return;
    }

    try {
      // Show loading toast
      const loadingToast = toast.info("Processing your request...", {
        autoClose: false,
      });

      // Find the approval request ID
      let requestId = null;

      // Check if we have pending requests loaded
      if (pendingApprovalRequests.length > 0) {
        const request = pendingApprovalRequests.find(
          (req) =>
            String(req.document_id) === String(documentToApprove.id) &&
            req.document_type === documentToApprove.type
        );

        if (request) {
          requestId = request.id;
        }
      }

      // If we couldn't find the request, try fetching it directly
      if (!requestId) {
        console.log("Fetching approval request directly:", documentToApprove);
        const response = await api.get(
          `/api/approval-requests/document/${documentToApprove.type}/${documentToApprove.id}`
        );

        if (
          response.data &&
          response.data.requests &&
          response.data.requests.length > 0
        ) {
          const pendingRequest = response.data.requests.find(
            (req) => req.status === "pending"
          );
          if (pendingRequest) {
            requestId = pendingRequest.id;
          }
        }
      }

      if (!requestId) {
        toast.dismiss(loadingToast);
        toast.error(
          "Couldn't find the approval request. Please refresh the page and try again."
        );
        return;
      }

      // Now make the API call with the request ID
      console.log(
        `Updating approval request ${requestId} with status: ${approvalAction}`
      );
      const approvalResponse = await api.patch(
        `/api/approval-requests/${requestId}`,
        {
          status: approvalAction,
          comments: approvalComment,
        }
      );

      toast.dismiss(loadingToast);

      if (approvalResponse.data && approvalResponse.data.success) {
        toast.success(
          `Document ${
            approvalAction === "approved" ? "approved" : "rejected"
          } successfully`
        );

        setShowApprovalActionModal(false);
        setDocumentToApprove(null);
        setApprovalAction(null);
        setApprovalComment("");

        // Refresh created documents to update statuses
        fetchCreatedDocuments();
      } else {
        throw new Error("Unexpected response from server");
      }
    } catch (error) {
      console.error("Error processing approval:", error);
      toast.error(
        "Failed to process approval request. " +
          (error.response?.data?.message || error.message)
      );
    }
  };

  if (loading) return <div className="loading">Loading project details...</div>;
  if (error) return <div className="error-message">{error}</div>;
  if (!project) return <div className="not-found">Project not found</div>;

  const formattedStartDate = project.start_date
    ? new Date(project.start_date).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : "Not specified";

  const formattedCreatedDate = project.created_at
    ? new Date(project.created_at).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : "Not specified";

  return (
    <div className="project-page">
      <Navbar role={role} />

      <div className="project-container">
        <div className="project-header">
          <div className="header-content">
            <h1>{project.title || "Untitled Project"}</h1>
            <p className="description">
              {project.description ||
                "No description available for this project."}
            </p>
            <div className="header-details">
              <span
                className={`status-badge ${(
                  project.status || "active"
                ).toLowerCase()}`}
              >
                {project.status || "Active"}
              </span>

              <div className="action-buttons">
                <button className="edit-button" onClick={handleEdit}>
                  <Edit size={16} />
                  Edit
                </button>
                <button className="delete-button" onClick={handleDelete}>
                  <Trash2 size={16} />
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="project-details">
          <div className="detail-section">
            <h2>Project Information</h2>
            <div className="detail-grid">
              <div className="detail-item">
                <div className="detail-icon">
                  <Calendar size={20} />
                </div>
                <div className="detail-content">
                  <label>Start Date</label>
                  <span>{formattedStartDate}</span>
                </div>
              </div>

              <div className="detail-item">
                <div className="detail-icon">
                  <Calendar size={20} />
                </div>
                <div className="detail-content">
                  <label>Created Date</label>
                  <span>{formattedCreatedDate}</span>
                </div>
              </div>

              <div className="detail-item">
                <div className="detail-icon">
                  <User size={20} />
                </div>
                <div className="detail-content">
                  <label>Created By</label>
                  <span>{project.user_name || "Unknown User"}</span>
                </div>
              </div>

              <div className="detail-item">
                <div className="detail-icon">
                  <Briefcase size={20} />
                </div>
                <div className="detail-content">
                  <label>Client</label>
                  <span>{project.clientname || "No client specified"}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Project Documents Section */}
          <div className="detail-section">
            <div className="section-header">
              <h2>Project Documents</h2>
              <button
                className="add-doc-button secondary-button"
                onClick={() => setShowDocModal(true)}
              >
                <Plus size={16} />
                Add Document
              </button>
            </div>

            <div className="documents-grid">
              {documentsLoading ? (
                <div className="loading-documents">Loading documents...</div>
              ) : documents.length > 0 ? (
                documents.map((doc) => (
                  <div className="document-card" key={doc.id}>
                    <div className="document-icon">
                      <FileText size={24} />
                    </div>
                    <div className="document-info">
                      <h3>{doc.title}</h3>
                      <p>{doc.file_name}</p>
                      <span className="document-date">
                        {new Date(doc.created_at).toLocaleDateString()}
                      </span>
                    </div>
                    <div className="document-actions">
                      <button
                        className="doc-action-btn download"
                        onClick={() =>
                          handleDownloadDocument(doc.id, doc.file_name)
                        }
                        title="Download"
                      >
                        <Download size={16} />
                      </button>
                      <button
                        className="doc-action-btn delete"
                        onClick={() => handleDeleteDocument(doc.id)}
                        title="Delete"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                ))
              ) : (
                <div className="no-documents">
                  No documents available for this project.
                </div>
              )}
            </div>
          </div>
        </div>
        {/* Document creation buttons */}
        <div className="detail-section created-documents-section">
          <div className="section-header">
            <h2>Document Creation</h2>
          </div>

          <div className="created-documents-container">
            {/* Common Application Form */}
            <div className="created-document-type">
              <h3>Common Application Form</h3>
              <div className="created-documents-list">
                {createdDocuments.caf && createdDocuments.caf.length > 0 ? (
                  <div className="created-document-card">
                    <div className="created-document-icon">
                      <FileText size={28} />
                    </div>
                    <div className="created-document-info">
                      <h4>
                        {createdDocuments.caf[0].title ||
                          "Common Application Form"}
                      </h4>
                      <div>
                        <span className="created-date">
                          {new Date(
                            createdDocuments.caf[0].created_at
                          ).toLocaleDateString()}
                        </span>
                        {/* Show approval status if it exists */}
                        {createdDocuments.caf[0].approval_status && (
                          <span
                            className={`approval-status ${createdDocuments.caf[0].approval_status}`}
                          >
                            {createdDocuments.caf[0].approval_status ===
                            "pending"
                              ? "Pending Approval"
                              : createdDocuments.caf[0].approval_status ===
                                "approved"
                              ? "Approved"
                              : "Rejected"}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="document-actions-wrapper">
                      <div className="document-actions">
                        {/* View button */}
                        <Link
                          to={`/caf/view/${createdDocuments.caf[0].id}`}
                          className="doc-action-btn view"
                          title="View"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <FileText size={16} />
                        </Link>

                        {/* Edit button - only if not approved */}
                        {(!createdDocuments.caf[0].approval_status ||
                          createdDocuments.caf[0].approval_status !==
                            "approved") && (
                          <button
                            className="doc-action-btn edit"
                            title="Edit"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/${role}/caf?projectId=${projectId}`);
                            }}
                          >
                            <Edit size={16} />
                          </button>
                        )}

                        {/* Request Approval button - only for project associate and if not already submitted */}
                        {role === "project_associate" &&
                          (!createdDocuments.caf[0].approval_status ||
                            createdDocuments.caf[0].approval_status ===
                              "rejected") && (
                            <button
                              className="doc-action-btn request-approval"
                              title="Request Approval"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRequestApproval(
                                  createdDocuments.caf[0].id,
                                  "caf"
                                );
                              }}
                            >
                              <User size={16} />
                            </button>
                          )}

                        {/* Delete button - always available */}
                        <button
                          className="doc-action-btn delete"
                          title="Delete"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (
                              createdDocuments.caf[0].approval_status ===
                              "approved"
                            ) {
                              if (
                                window.confirm(
                                  "This document has been approved. Are you sure you want to delete it? This action cannot be undone."
                                )
                              ) {
                                handleDeleteDocument(
                                  createdDocuments.caf[0].id,
                                  "caf"
                                );
                              }
                            } else {
                              handleDeleteDocument(
                                createdDocuments.caf[0].id,
                                "caf"
                              );
                            }
                          }}
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                      {role === "admin" &&
                        createdDocuments.caf &&
                        createdDocuments.caf.length > 0 &&
                        createdDocuments.caf[0].approval_status ===
                          "pending" && (
                          <div className="admin-approval-actions">
                            <button
                              className="doc-action-btn approve"
                              title="Approve"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleApprovalAction(
                                  createdDocuments.caf[0].id,
                                  "caf",
                                  "approved"
                                );
                              }}
                            >
                              <Check size={16} />
                            </button>
                            <button
                              className="doc-action-btn reject"
                              title="Reject"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleApprovalAction(
                                  createdDocuments.caf[0].id,
                                  "caf",
                                  "rejected"
                                );
                              }}
                            >
                              <X size={16} />
                            </button>
                          </div>
                        )}
                    </div>
                  </div>
                ) : (
                  <div
                    className="no-document-card"
                    onClick={() => handleDocumentTypeSelect("caf")}
                  >
                    <div className="no-document-icon">
                      <Plus size={20} />
                    </div>
                    <p>Create Common Application Form</p>
                  </div>
                )}
              </div>
            </div>
            {/* Pre-Feasibility Report */}
            <div className="created-document-type">
              <h3>Pre-Feasibility Report</h3>
              <div className="created-documents-list">
                {createdDocuments.pfr && createdDocuments.pfr.length > 0 ? (
                  <div className="created-document-card">
                    <div className="created-document-icon">
                      <FileText size={28} />
                    </div>
                    <div className="created-document-info">
                      <h4>
                        {createdDocuments.pfr[0].title ||
                          "Pre-Feasibility Report"}
                      </h4>
                      <div>
                        <span className="created-date">
                          {new Date(
                            createdDocuments.pfr[0].created_at
                          ).toLocaleDateString()}
                        </span>
                        {/* Show approval status if it exists */}
                        {createdDocuments.pfr[0].approval_status && (
                          <span
                            className={`approval-status ${createdDocuments.pfr[0].approval_status}`}
                          >
                            {createdDocuments.pfr[0].approval_status ===
                            "pending"
                              ? "Pending Approval"
                              : createdDocuments.pfr[0].approval_status ===
                                "approved"
                              ? "Approved"
                              : "Rejected"}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="document-actions-wrapper">
                      <div className="document-actions">
                        {/* Edit button - only if not approved */}
                        {(!createdDocuments.pfr[0].approval_status ||
                          createdDocuments.pfr[0].approval_status !==
                            "approved") && (
                          <button
                            className="doc-action-btn edit"
                            title="Edit"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/${role}/pfr?projectId=${projectId}`);
                            }}
                          >
                            <Edit size={16} />
                          </button>
                        )}

                        {/* Download button */}
                        <button
                          className="doc-action-btn download"
                          title="Download"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDownloadPfrDocument(
                              createdDocuments.pfr[0].id
                            );
                          }}
                        >
                          <Download size={16} />
                        </button>

                        {/* Request Approval button - only for project associate and if not already submitted */}
                        {role === "project_associate" &&
                          (!createdDocuments.pfr[0].approval_status ||
                            createdDocuments.pfr[0].approval_status ===
                              "rejected") && (
                            <button
                              className="doc-action-btn request-approval"
                              title="Request Approval"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRequestApproval(
                                  createdDocuments.pfr[0].id,
                                  "pfr"
                                );
                              }}
                            >
                              <User size={16} />
                            </button>
                          )}

                        {/* Delete button - always available */}
                        <button
                          className="doc-action-btn delete"
                          title="Delete"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (
                              createdDocuments.pfr[0].approval_status ===
                              "approved"
                            ) {
                              if (
                                window.confirm(
                                  "This document has been approved. Are you sure you want to delete it? This action cannot be undone."
                                )
                              ) {
                                handleDeleteDocument(
                                  createdDocuments.pfr[0].id,
                                  "pfr"
                                );
                              }
                            } else {
                              handleDeleteDocument(
                                createdDocuments.pfr[0].id,
                                "pfr"
                              );
                            }
                          }}
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                      {role === "admin" &&
                        createdDocuments.pfr &&
                        createdDocuments.pfr.length > 0 &&
                        createdDocuments.pfr[0].approval_status ===
                          "pending" && (
                          <div className="admin-approval-actions">
                            <button
                              className="doc-action-btn approve"
                              title="Approve"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleApprovalAction(
                                  createdDocuments.pfr[0].id,
                                  "pfr",
                                  "approved"
                                );
                              }}
                            >
                              <Check size={16} />
                            </button>
                            <button
                              className="doc-action-btn reject"
                              title="Reject"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleApprovalAction(
                                  createdDocuments.pfr[0].id,
                                  "pfr",
                                  "rejected"
                                );
                              }}
                            >
                              <X size={16} />
                            </button>
                          </div>
                        )}
                    </div>
                  </div>
                ) : (
                  <div
                    className="no-document-card"
                    onClick={() => handleDocumentTypeSelect("pfr")}
                  >
                    <div className="no-document-icon">
                      <Plus size={20} />
                    </div>
                    <p>Create Pre-Feasibility Report</p>
                  </div>
                )}
              </div>
            </div>
            {/* Environment Management Plan */}
            {/* <div className="created-document-type">
              <h3>Environment Management Plan</h3>
              <div className="created-documents-list">
                {createdDocuments.emp && createdDocuments.emp.length > 0 ? (
                  <div className="created-document-card">
                    <div className="created-document-icon">
                      <FileText size={28} />
                    </div>
                    <div className="created-document-info">
                      <h4>
                        {createdDocuments.emp[0].title ||
                          "Environment Management Plan"}
                      </h4>
                      <div>
                        <span className="created-date">
                          {new Date(
                            createdDocuments.emp[0].created_at
                          ).toLocaleDateString()}
                        </span>
                        {createdDocuments.emp[0].approval_status && (
                          <span
                            className={`approval-status ${createdDocuments.emp[0].approval_status}`}
                          >
                            {createdDocuments.emp[0].approval_status ===
                            "pending"
                              ? "Pending Approval"
                              : createdDocuments.emp[0].approval_status ===
                                "approved"
                              ? "Approved"
                              : "Rejected"}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="document-actions-wrapper">
                      <div className="document-actions">
                        {(!createdDocuments.emp[0].approval_status ||
                          createdDocuments.emp[0].approval_status !==
                            "approved") && (
                          <button
                            className="doc-action-btn edit"
                            title="Edit"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/${role}/emp?projectId=${projectId}`);
                            }}
                          >
                            <Edit size={16} />
                          </button>
                        )}
                        <button
                          className="doc-action-btn download"
                          title="Download"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDownloadEmpDocument(
                              createdDocuments.emp[0].id
                            );
                          }}
                        >
                          <Download size={16} />
                        </button>
                        {role === "project_associate" &&
                          (!createdDocuments.emp[0].approval_status ||
                            createdDocuments.emp[0].approval_status ===
                              "rejected") && (
                            <button
                              className="doc-action-btn request-approval"
                              title="Request Approval"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRequestApproval(
                                  createdDocuments.emp[0].id,
                                  "emp"
                                );
                              }}
                            >
                              <User size={16} />
                            </button>
                          )}
                        <button
                          className="doc-action-btn delete"
                          title="Delete"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (
                              createdDocuments.emp[0].approval_status ===
                              "approved"
                            ) {
                              if (
                                window.confirm(
                                  "This document has been approved. Are you sure you want to delete it? This action cannot be undone."
                                )
                              ) {
                                handleDeleteDocument(
                                  createdDocuments.emp[0].id,
                                  "emp"
                                );
                              }
                            } else {
                              handleDeleteDocument(
                                createdDocuments.emp[0].id,
                                "emp"
                              );
                            }
                          }}
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                      {role === "admin" &&
                        createdDocuments.emp &&
                        createdDocuments.emp.length > 0 &&
                        createdDocuments.emp[0].approval_status ===
                          "pending" && (
                          <div className="admin-approval-actions">
                            <button
                              className="doc-action-btn approve"
                              title="Approve"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleApprovalAction(
                                  createdDocuments.emp[0].id,
                                  "emp",
                                  "approved"
                                );
                              }}
                            >
                              <Check size={16} />
                            </button>
                            <button
                              className="doc-action-btn reject"
                              title="Reject"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleApprovalAction(
                                  createdDocuments.emp[0].id,
                                  "emp",
                                  "rejected"
                                );
                              }}
                            >
                              <X size={16} />
                            </button>
                          </div>
                        )}
                    </div>
                  </div>
                ) : (
                  <div
                    className="no-document-card"
                    onClick={() => handleDocumentTypeSelect("emp")}
                  >
                    <div className="no-document-icon">
                      <Plus size={20} />
                    </div>
                    <p>Create Environment Management Plan</p>
                  </div>
                )}
              </div>
            </div> */}
          </div>
        </div>
      </div>

      {/* Document Upload Modal */}
      {showDocModal && (
        <div className="modal-overlay">
          <div className="document-modal">
            <div className="modal-header">
              <h2>Add Document</h2>
              <button
                className="close-modal"
                onClick={() => setShowDocModal(false)}
              >
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleDocumentUpload}>
              <div className="form-group modal-form-group">
                <label htmlFor="docTitle" className="required">
                  Document Title
                </label>
                <input
                  id="docTitle"
                  type="text"
                  value={docTitle}
                  onChange={(e) => setDocTitle(e.target.value)}
                  placeholder="Enter document title"
                  required
                />
              </div>

              <div className="form-group modal-form-group file-upload-group">
                <label htmlFor="docFile" className="required">
                  Upload File
                </label>
                <div
                  className="file-upload-area"
                  onClick={() => document.getElementById("docFile").click()}
                >
                  <input
                    id="docFile"
                    type="file"
                    onChange={handleFileChange}
                    required
                    className="file-input"
                  />
                  <div className="upload-icon">
                    <FileText size={24} />
                    <span>Click to browse files</span>
                  </div>
                </div>
                {docFile && (
                  <div className="file-info">
                    <span className="file-name">{docFile.name}</span>
                    <span className="file-size">
                      {(docFile.size / 1024).toFixed(2)} KB
                    </span>
                    <button
                      type="button"
                      className="remove-file-btn"
                      onClick={clearSelectedFile}
                      title="Remove file"
                    >
                      <X size={16} />
                    </button>
                  </div>
                )}
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  className="cancel-button"
                  onClick={() => setShowDocModal(false)}
                  disabled={uploadingDoc}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="submit-button"
                  disabled={uploadingDoc || !docFile}
                >
                  {uploadingDoc ? "Uploading..." : "Upload"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Approval Request Modal */}
      {showApprovalModal && (
        <div className="modal-overlay">
          <div className="document-modal">
            <div className="modal-header">
              <h2>Request Document Approval</h2>
              <button
                className="close-modal"
                onClick={() => setShowApprovalModal(false)}
              >
                <X size={20} />
              </button>
            </div>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                submitApprovalRequest();
              }}
            >
              <div className="form-group modal-form-group">
                <label htmlFor="adminSelect" className="required">
                  Select Admin for Approval
                </label>
                <select
                  id="adminSelect"
                  value={selectedAdmin}
                  onChange={(e) => setSelectedAdmin(e.target.value)}
                  required
                  className="admin-select"
                >
                  <option value="">-- Select Admin --</option>
                  {adminsList.map((admin) => (
                    <option key={admin.id} value={admin.id}>
                      {admin.name} ({admin.email})
                    </option>
                  ))}
                </select>
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  className="cancel-button"
                  onClick={() => setShowApprovalModal(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="submit-button"
                  disabled={!selectedAdmin}
                >
                  Submit for Approval
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Approval Action Modal */}
      {showApprovalActionModal && (
        <div className="modal-overlay">
          <div className="document-modal">
            <div className="modal-header">
              <h2>
                {approvalAction === "approved" ? "Approve" : "Reject"} Document
              </h2>
              <button
                className="close-modal"
                onClick={() => setShowApprovalActionModal(false)}
              >
                <X size={20} />
              </button>
            </div>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                submitApprovalDecision();
              }}
            >
              <div className="form-group modal-form-group">
                <div className={`approval-action-message ${approvalAction}`}>
                  <div className="action-icon">
                    {approvalAction === "approved" ? (
                      <Check size={20} />
                    ) : (
                      <AlertTriangle size={20} />
                    )}
                  </div>
                  <p>
                    {approvalAction === "approved"
                      ? "You are approving this document. This will mark the document as approved and notify the requester."
                      : "You are rejecting this document. The requester will need to make changes and submit for approval again."}
                  </p>
                </div>

                <label htmlFor="approvalComment">
                  Comments{" "}
                  {approvalAction === "rejected" && (
                    <span className="required-indicator">*</span>
                  )}
                </label>
                <textarea
                  id="approvalComment"
                  value={approvalComment}
                  onChange={(e) => setApprovalComment(e.target.value)}
                  placeholder={
                    approvalAction === "approved"
                      ? "Add any comments (optional)"
                      : "Please provide a reason for rejection"
                  }
                  required={approvalAction === "rejected"}
                  rows={4}
                />
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  className="cancel-button"
                  onClick={() => setShowApprovalActionModal(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className={`submit-button ${
                    approvalAction === "approved"
                      ? "approve-button"
                      : "reject-button"
                  }`}
                >
                  {approvalAction === "approved" ? "Approve" : "Reject"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectPage;
